<template>
  <div v-if="canLoadPayslipSnapshot" class="row mb-3">
    <div class="col-12">
      <div class="card">
        <div
          class="card-body d-flex flex-wrap align-items-center justify-content-between gap-3"
        >
          <div class="flex-grow-1 min-w-0">
            <h6 class="mb-1">Latest payslip</h6>
            <template v-if="loading">
              <p class="text-muted small mb-0">Loading…</p>
            </template>
            <template v-else-if="latest">
              <p class="mb-1">
                <span class="text-muted">{{ latest.periodLabel }}</span>
                <span class="mx-2 text-muted">·</span>
                <span class="fw-semibold text-gray-9">
                  {{ currency }}
                  {{ formatMoney(latest.netPay) }}
                </span>
                <span class="text-muted small ms-1">net pay</span>
              </p>
              <p class="text-muted small mb-0">Most recent approved payslip in your account.</p>
            </template>
            <template v-else>
              <p class="text-muted small mb-0">
                No payslips yet. They will appear here after payroll is approved.
              </p>
            </template>
          </div>
          <div class="d-flex flex-wrap gap-2 flex-shrink-0">
            <button
              v-if="latest?.payslipId"
              type="button"
              class="btn btn-outline-primary btn-sm"
              :disabled="downloading"
              @click="downloadLatest"
            >
              Download PDF
            </button>
            <router-link
              to="/payroll/my-payslips"
              class="btn btn-primary btn-sm"
            >
              View all payslips
            </router-link>
            <router-link
              to="/payroll/my-p9"
              class="btn btn-outline-primary btn-sm"
            >
              My P9
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import moment from "moment";
import reportsService from "@/services/reports.service";
import { useEmployeeSalaryStore } from "@/stores/employeeSalaryStore";
import { useEmployeeDashboardAccess } from "@/composables/useEmployeeDashboardAccess";
import { toast } from "@/utils/toast";

const { canLoadPayslipSnapshot } = useEmployeeDashboardAccess();
const salaryStore = useEmployeeSalaryStore();

const loading = ref(false);
const downloading = ref(false);
const latest = ref(null);
const currency = ref("KES");

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function mapRow(row) {
  if (!row) return null;
  const period =
    row.period || row.payroll_run?.period || row.payrollRun?.period || "";
  const periodLabel =
    row.period_label ||
    (row.month_label && row.year
      ? `${row.month_label} ${row.year}`
      : period || "—");
  return {
    period,
    periodLabel,
    netPay: row.net_pay ?? 0,
    payslipId: row.payslip?.id || row.payslip_id || null,
    historical: !!(row.historical || row.source === "imported_history"),
  };
}

async function downloadLatest() {
  const row = latest.value;
  if (!row) return;
  downloading.value = true;
  try {
    if (row.historical) {
      const res = await reportsService.downloadMyHistoricalPayslip({
        period: row.period,
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payslip-${row.period}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return;
    }
    if (row.payslipId) {
      await salaryStore.downloadMyPayslip(row.payslipId, {
        period: row.periodLabel,
      });
    }
  } catch (err) {
    toast(err?.response?.data?.message || "Could not download payslip.", "error");
  } finally {
    downloading.value = false;
  }
}

onMounted(async () => {
  if (!canLoadPayslipSnapshot.value) return;
  loading.value = true;
  try {
    const to = moment().format("YYYY-MM");
    const from = moment().subtract(24, "months").format("YYYY-MM");
    const { data } = await reportsService.myPayslips({
      from,
      to,
      page: 1,
      per_page: 1,
    });
    currency.value = data?.summary?.currency || "KES";
    const row = data?.rows?.data?.[0];
    latest.value = mapRow(row);
  } catch {
    latest.value = null;
  } finally {
    loading.value = false;
  }
});
</script>
